import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Layers, Plus, Trash2, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const typeLabels = { fixed: 'Fixed Bundle', quantity_break: 'Quantity Break', bogo: 'BOGO', mix_match: 'Mix & Match' };
const typeColors = { fixed: '#2563eb', quantity_break: '#7c3aed', bogo: '#E85D3A', mix_match: '#059669' };

export default function BundleListPage() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBundles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bundles');
      setBundles(data);
    } catch { toast.error('Failed to load bundles'); }
    setLoading(false);
  };

  useEffect(() => { fetchBundles(); }, []);

  const deleteBundle = async (id) => {
    if (!window.confirm('Delete this bundle?')) return;
    try {
      await api.delete(`/bundles/${id}`);
      toast.success('Bundle deleted');
      fetchBundles();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <motion.div data-testid="bundle-list-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Bundles</h1>
          <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{bundles.length} bundles</p>
        </div>
        <Button data-testid="add-bundle-btn" onClick={() => navigate('/admin/bundles/new')}
          className="font-sans text-sm gap-2 rounded-none" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
          <Plus size={15} strokeWidth={1.5} /> New Bundle
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>Bundle</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium hidden sm:table-cell" style={{ color: 'rgba(26,26,26,0.45)' }}>Type</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium hidden md:table-cell" style={{ color: 'rgba(26,26,26,0.45)' }}>Products</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium hidden md:table-cell" style={{ color: 'rgba(26,26,26,0.45)' }}>Discount</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>Status</th>
              <th className="w-10 p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</td></tr>
            ) : bundles.length === 0 ? (
              <tr><td colSpan={6} className="p-12 text-center">
                <Layers size={32} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.2)' }} />
                <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No bundles yet</p>
              </td></tr>
            ) : bundles.map(b => (
              <tr key={b.id} className="border-t hover:bg-[rgba(26,26,26,0.01)] transition-colors" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                <td className="p-3">
                  <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{b.title}</p>
                </td>
                <td className="p-3 hidden sm:table-cell">
                  <Badge className="rounded-sm font-sans text-[10px] uppercase tracking-wider font-medium"
                    style={{ backgroundColor: `${typeColors[b.type]}15`, color: typeColors[b.type], border: 'none' }}>
                    {typeLabels[b.type] || b.type}
                  </Badge>
                </td>
                <td className="p-3 hidden md:table-cell">
                  <span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{b.products?.length || 0} items</span>
                </td>
                <td className="p-3 hidden md:table-cell">
                  <span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>
                    {b.discount_type === 'percentage' ? `${b.discount_value}%` : b.discount_type === 'fixed_price' ? `$${b.discount_value}` : `$${b.discount_value} off`}
                  </span>
                </td>
                <td className="p-3">
                  <Badge className="rounded-sm font-sans text-[10px] uppercase tracking-wider font-medium"
                    style={{ backgroundColor: b.status === 'active' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)', color: b.status === 'active' ? '#16a34a' : '#ca8a04', border: 'none' }}>
                    {b.status}
                  </Badge>
                </td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`bundle-actions-${b.id}`} className="p-1 rounded hover:bg-[rgba(26,26,26,0.05)] transition-colors">
                        <MoreHorizontal size={16} strokeWidth={1.5} style={{ color: 'rgba(26,26,26,0.4)' }} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/bundles/${b.id}/edit`)} className="font-sans text-sm">Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => deleteBundle(b.id)} className="font-sans text-sm text-red-600">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
